// 급여관리 PRO — 거래처 월간 현황 매트릭스 화면용 도메인 모델.
// API 정의서: BackEnd/docs/api/payroll-management-api.md
// 판정 로직 근거: ~/Downloads/세특개발요청사항/7.(클로드)급여관리PRO_화면설명서.md §5

// ---------------------------------------------------------------------------
// 표시 상태 코드 (화면설명서 §4)
// ---------------------------------------------------------------------------
// 내부 판정 결과는 아래 코드 중 하나로 수렴하고, STATUS_META에서 4가지 표시(dot)로 매핑된다.
export type StatusCode =
  | "done" // 완료
  | "ok" // 입력완료
  | "sent" // 전송완료
  | "filed" // 신고완료
  | "att" // 첨부
  | "sub" // 제출완료
  | "refund" // 환급(0 이하) — 납부 불필요
  | "req" // 퇴직금 지급예정월 — 담당자 직접 입력 필요
  | "todo" // 미첨부
  | "nfile" // 미신고
  | "nsend" // 미전송
  | "nsub" // 미제출
  | "nmail" // 미발송
  | "redo" // 수정 재발송 필요
  | "empty" // 임시저장
  | "confirm" // 거래처가 확인요청을 보내고 회신 대기 (§2.17 CONFIRM 대기)
  | "reopen" // 마감됐지만 거래처가 마감 해제를 요청함 (§2.17 REOPEN 대기)
  | "alert" // 확인요망 (소득·납부서 불일치)
  | "fail" // 전송실패
  | "off"; // 해당없음

export type StatusKind = "done" | "todo" | "alert" | "off";

export interface StatusMeta {
  // dot 색상 클래스 성격
  dot: StatusKind;
  text: string;
  // 정렬·완료판정용 성격
  kind: StatusKind;
}

export const STATUS_META: Record<StatusCode, StatusMeta> = {
  done: { dot: "done", text: "완료", kind: "done" },
  ok: { dot: "done", text: "입력완료", kind: "done" },
  sent: { dot: "done", text: "전송완료", kind: "done" },
  filed: { dot: "done", text: "신고완료", kind: "done" },
  att: { dot: "done", text: "첨부", kind: "done" },
  sub: { dot: "done", text: "제출완료", kind: "done" },
  refund: { dot: "done", text: "환급", kind: "done" },
  req: {
    dot: "todo",
    text: "퇴직금 지급예정월 — 담당자가 세무사랑에 직접 입력해야 합니다",
    kind: "todo"
  },
  todo: { dot: "todo", text: "미첨부", kind: "todo" },
  nfile: { dot: "todo", text: "미신고", kind: "todo" },
  nsend: { dot: "todo", text: "미전송", kind: "todo" },
  nsub: { dot: "todo", text: "미제출", kind: "todo" },
  nmail: { dot: "todo", text: "미발송", kind: "todo" },
  redo: {
    dot: "todo",
    text: "수정 재발송 필요 — 재신고로 납부서가 갱신됨",
    kind: "todo"
  },
  empty: { dot: "todo", text: "임시저장", kind: "todo" },
  confirm: {
    dot: "todo",
    text: "확인요청중 — 거래처가 확인요청을 보냈습니다",
    kind: "todo"
  },
  reopen: {
    dot: "todo",
    text: "마감 해제 요청 — 거래처가 마감 해제를 요청했습니다",
    kind: "todo"
  },
  alert: { dot: "alert", text: "확인요망", kind: "alert" },
  fail: { dot: "alert", text: "전송실패", kind: "alert" },
  off: { dot: "off", text: "–", kind: "off" }
};

export const statusKind = (code: StatusCode | undefined): StatusKind =>
  (STATUS_META[code as StatusCode] || STATUS_META.off).kind;

// ---------------------------------------------------------------------------
// 담당자 수동 체크 칸 (API §3.3 TaskCell)
// ---------------------------------------------------------------------------
export type TaskCell =
  | "WITHHOLDING_TAX_FILED"
  | "INSURANCE_DAILY_WORK_REPORT"
  | "MONTHLY_STATEMENT_DAILY"
  | "MONTHLY_STATEMENT_BUSINESS_SIMPLE"
  | "MONTHLY_STATEMENT_OTHER_SIMPLE"
  | "ANNUAL_STATEMENT_EARNED_SIMPLE"
  | "ANNUAL_STATEMENT_EARNED"
  | "ANNUAL_STATEMENT_BUSINESS"
  | "ANNUAL_STATEMENT_RETIREMENT"
  | "ANNUAL_STATEMENT_OTHER";

export interface ManualCheck {
  done: boolean;
  checkedAt: string | null;
  checkedByUser: string | null;
}

// ---------------------------------------------------------------------------
// 그리드 조회 응답 (API §3.1 PayrollGridRow)
// ---------------------------------------------------------------------------
export type FilingCycle = "MONTHLY" | "SEMIANNUAL";
export type ReportBasis = "CURRENT_MONTH" | "PREV_MONTH";
export type EntryStatus = "NOT_ENTERED" | "ENTERED" | "CLOSED";
export type PayeeType =
  | "EMPLOYEE"
  | "DAILY_WORKER"
  | "BUSINESS_INCOME"
  | "OTHER_INCOME";

// 급여자료 입력·마감 상태 (근로/일용/사업/기타)
export interface PayrollStatus {
  earned: EntryStatus;
  daily: EntryStatus;
  biz: EntryStatus;
  etc: EntryStatus;
}

// 세무사랑 업로드 일시 (null = 미전송)
export interface PayrollTransmission {
  earned: string | null;
  daily: string | null;
  biz: string | null;
  etc: string | null;
}

export interface NoticeItem {
  docType: "NATIONAL_TAX" | "LOCAL_TAX" | "TAX_RECEIPT" | "OTHER";
  taxItem: string;
  // 근로/사업/기타/퇴직/이자/배당/연금소득/해당없음/미상
  incomeType: string;
  amount: number;
  // 납부서 업로드(등록) 일시. 발송 시각보다 나중이면 재발송 필요 (§5.5 redo)
  uploadedAt: string | null;
}

export type MemoRepeat = "NONE" | "MONTHLY" | "YEARLY";

// 그리드·메모 API 응답의 PayrollMemoView — 특정 지급월이 속한 주기 기준으로 done을 판정한 형태.
// 저장 원본(PayrollMemo)은 done 대신 주기별 completions 배열을 갖지만 프론트는 이 뷰만 소비한다.
// 반복 메모는 배치 초기화 없이, 주기 키(MONTHLY=yyyy-MM 등)에 완료 기록이 없으면 자연히 활성.
export interface PayrollMemo {
  id: string;
  text: string;
  repeat: MemoRepeat;
  repeatMonth: number | null;
  done: boolean;
  doneDate: string | null;
  doneByUser: string | null;
}

export interface PayrollGridRow {
  businessId: string;
  name: string;
  regNumber: string;
  incharge: string | null;
  filingCycle: FilingCycle;
  reportBasis: ReportBasis;
  // 급여 지급일 (Business.salaryDay). 0 = 말일, null = 미설정
  salaryDay?: number | null;
  payrollStatus: PayrollStatus;
  transmission: PayrollTransmission;
  notices: NoticeItem[];
  noticeKakaoSentAt: string | null;
  manualChecks: Partial<Record<TaskCell, ManualCheck>>;
  // 급여자료 퇴직 칸 — 서버가 정산요청 payMonth == 화면 지급월 기준으로 판정.
  //   OFF = 지급예정월 아님/퇴직금 없음/1년 미만/DC, REQUIRED = 도래·미처리, DONE = 대상 전원 정산완료
  retirement: RetirementStatus;
  memos: PayrollMemo[];
  // 공단 취득/상실 칸 (읽기 전용) — 직원별 요청·처리 기록(§2.15/2.16)을 모아 서버가 판정.
  // 대상 = 노출 기간 안 일반근로자 전원 (거래처 요청 여부 무관).
  //   OFF = 대상 직원 없음, REQUIRED = 미처리 직원 있음, DONE = 전원 처리됨
  insuranceAcquisition: InsuranceCell;
  insuranceLoss: InsuranceCell;
}

export type RetirementStatus = "OFF" | "REQUIRED" | "DONE";
export type InsuranceCell = "OFF" | "REQUIRED" | "DONE";

// ---------------------------------------------------------------------------
// 퇴사자 정산요청 패널 (API §2.10 GET /severance-requests — 조회 전용, 탭 2개)
// 세특 [급여입력] 팝업에서 거래처가 입력한 Payee.severanceRequest 를 지급월 기준으로 정리한 뷰.
// ---------------------------------------------------------------------------
export type PensionType = "NONE" | "DB" | "DC";
export type WageProgress = "CLOSED" | "PENDING"; // 탭1 진행: 세특 마감완료 / 세특 정산 대기

// §3.7 SeveranceRequestView
export interface SeveranceRequestView {
  businessId: string;
  businessName: string;
  payeeId: string;
  payeeName: string;
  hireDate: string | null; // YYYY-MM-DD (Payee.workStart)
  leaveDate: string; // YYYY-MM-DD (Payee.workFinish = 마지막 근로일)
  // 퇴사사유 (Payee.resignReasonCode/Text — 상실신고·재직상태 변경과 공용, 정산요청이 없어도 내려옴).
  // 경로에 따라 코드만 있고 text는 비어 있을 수 있다
  leaveReasonCode: string | null;
  leaveReasonText: string | null;
  requested: boolean; // 세특 팝업에서 정산요청이 저장됐는지
  severanceEligible: boolean; // 계속근로 1년 이상
  // 탭1
  wageProgress: WageProgress;
  // 탭2 (정산요청 값)
  willPay: boolean | null;
  pensionType: PensionType | null;
  payMonth: string | null; // YYYY-MM (지급예정월)
  payMonthIsCurrent: boolean; // 지급예정월 == 화면 지급월
  withholdingTarget: boolean; // 원천세 신고 대상 (DC=false)
  toIrp: boolean | null;
  irpBank: string | null;
  irpAccount: string | null;
  irpHolder: string | null;
  requestedAt: string | null;
  settledAt: string | null; // 담당자가 퇴직 칸을 눌러 완료한 시각 (null = 미처리)
}

// §2.10 응답
export interface SeveranceRequestPanel {
  incomeTaxSettlements: SeveranceRequestView[]; // 탭1 — 이 지급월에 담기는 퇴사자 전원, 퇴사일 순
  severancePaySettlements: SeveranceRequestView[]; // 탭2 — 퇴직금 지급 접수 건 전부, 지급예정월 순
}

// ---------------------------------------------------------------------------
// 공단 취득·상실신고 패널 (API §2.15 GET /insurance-reports, §2.16 PATCH /insurance-filing)
// 거래처가 세특 [지급대상자]에서 넣은 신고 요청 + 담당자 처리 여부. 직원별로 완료 체크/해제한다.
// ---------------------------------------------------------------------------
export type InsuranceKind = "ACQUISITION" | "LOSS";
// REQUEST = 세무특공대에 신고 요청 / SELF_FILED = 회사가 직접 신고 / null = 요청 없음
export type InsuranceReportMode = "REQUEST" | "SELF_FILED";

// §3.8 InsuranceReportView — 취득 행에는 상실 필드가, 상실 행에는 취득 필드가 비어 있다.
export interface InsuranceReportView {
  businessId: string;
  businessName: string;
  payeeId: string;
  payeeName: string;
  hireDate: string | null; // YYYY-MM-DD (Payee.workStart = 취득일)
  leaveDate: string | null; // YYYY-MM-DD (Payee.workFinish = 마지막 근로일)
  mode: InsuranceReportMode | null;
  requestedAt: string | null;
  requestedByUser: string | null;
  filed: boolean; // 담당자 체크했거나 회사 직접 신고면 true
  locked: boolean; // 회사 직접 신고 → 체크/해제 불가
  filedAt: string | null;
  filedByUser: string | null;
  // 취득
  monthlyRemuneration?: number | null; // 보수월액(비과세 제외), REQUEST만
  weeklyWorkHour?: number | null;
  occupation?: string | null;
  occupationCode?: string | null;
  contractWorker?: boolean | null;
  contractEndMonth?: string | null; // YYYY-MM
  // 상실
  lossDate?: string | null; // 상실일 = 퇴사일 다음날
  lossReasonCode?: string | null;
  lossReasonText?: string | null;
}

// §2.15 응답
export interface InsuranceReportPanel {
  acquisitions: InsuranceReportView[]; // 입사일 순
  losses: InsuranceReportView[]; // 퇴사일 순
}

// §2.17 PayrollConfirmRequestView — 세특 급여입력(간편입력)에서 거래처가 보낸 요청
export type ConfirmRequestKind = "CONFIRM" | "REOPEN"; // 확인요청(사람별) / 마감 해제 요청(그 달 단위)
export type ConfirmRequestStatus = "PENDING" | "ANSWERED";

export interface PayrollConfirmRequestView {
  id: string;
  businessId: string;
  businessName: string;
  kind: ConfirmRequestKind;
  // REOPEN 은 모두 null
  payeeId: string | null;
  payeeType: PayeeType | null;
  payeeName: string | null;
  accrualMonth: string; // 귀속연월 YYYYMM (요청이 저장된 달)
  paymentMonth: string; // 지급월 YYYYMM
  filingMonth: string | null; // 신고월 YYYYMM. 신고주기 NONE 이면 null
  memo: string | null;
  autoReasons: string[];
  status: ConfirmRequestStatus;
  requestedAt: string;
  requestedByUser: string | null;
  requestedByName: string | null;
  reply: string | null; // 내용 없이 처리완료하면 null
  repliedAt: string | null;
  repliedByUser: string | null;
  repliedByName: string | null;
}

// ---------------------------------------------------------------------------
// 화면 파생 상태 (그리드 로직 grid.logic.ts에서 채운다)
// ---------------------------------------------------------------------------
export interface DerivedRow extends PayrollGridRow {
  // 컬럼 id → 표시 상태 코드
  cells: Record<string, StatusCode>;
  // 발송이력 유무
  dispatched: boolean;
  // 이 지급월에 담기는 귀속월 뱃지 텍스트
  basisLabel: "당월" | "전월";
  typeLabel: "월별" | "반기";
  // 지급월의 급여 지급일 "YY.MM.DD" (salaryDay 미설정이면 null). 화면 지급월이 필요해 index에서 병합한다.
  payDate?: string | null;
  // 알림톡 수신 번호 — grid API에 없어 납부서 현황 API에서 병합한다.
  // kakaoMobileNo(= Business.payrollManagerMobileNo 급여담당자 연락처, 수정 가능) 없으면
  // userMobileNo(조직 대표 사용자, 읽기 전용)를 표시한다.
  kakaoMobileNo?: string | null;
  userMobileNo?: string | null;
  // 거래처 자유 메모 (Organization.memoMap, payment-notice-api.md §4.3/4.4).
  // 할 일(memos) 체크리스트와 별개인 한 줄 메모. grid API에 없어 §4.4에서 병합한다.
  orgMemo?: string | null;
}
